import React from 'react';
import { Clock, AlertTriangle, Wallet, UserCheck, Bell, RotateCcw, X } from 'lucide-react';

interface ManualDepositWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  amount: number;
  providerName: string;
  isLoading?: boolean;
}

/**
 * Warning modal shown before depositing into a manual game (e.g. Golden Dragon).
 * Manual games require staff to process deposits, taking 5 minutes to 2 hours.
 */
const ManualDepositWarningModal: React.FC<ManualDepositWarningModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  amount,
  providerName,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal Content */}
      <div
        className="relative w-full max-w-md rounded-2xl border border-white/15 shadow-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.97) 0%, rgba(88, 28, 135, 0.95) 50%, rgba(15, 23, 42, 0.97) 100%)',
          backdropFilter: 'blur(20px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-amber-500/20 border border-amber-400/30">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <h2 className="text-lg font-bold text-white">Manual Deposit Notice</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-all"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Main warning message */}
          <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-400/20">
            <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-200/90 leading-relaxed">
              This game requires manual processing by our staff. Your deposit will be processed within{' '}
              <strong className="text-amber-300">5 minutes to 2 hours</strong>.
            </p>
          </div>

          {/* Info list */}
          <div className="space-y-3 p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-start gap-3">
              <Wallet className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-white/80">Funds reserved from wallet immediately</p>
            </div>
            <div className="flex items-start gap-3">
              <UserCheck className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-white/80">Staff deposits manually into game</p>
            </div>
            <div className="flex items-start gap-3">
              <Bell className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-white/80">Notification sent when confirmed</p>
            </div>
            <div className="flex items-start gap-3">
              <RotateCcw className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-white/80">Auto-refund after 2 hours if needed</p>
            </div>
          </div>

          {/* Amount & Provider summary */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider font-semibold mb-0.5">Amount</p>
              <p className="text-lg font-bold text-white">${amount.toFixed(2)}</p>
            </div>
            <div className="text-center">
              <span className="text-white/30 text-lg">&rarr;</span>
            </div>
            <div className="text-right">
              <p className="text-xs text-white/40 uppercase tracking-wider font-semibold mb-0.5">Game</p>
              <p className="text-lg font-bold text-white">{providerName}</p>
            </div>
          </div>
        </div>

        {/* Footer / Buttons */}
        <div className="flex items-center gap-3 p-5 border-t border-white/10">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-3 px-4 rounded-xl font-semibold text-sm text-white/80 border border-white/15 hover:bg-white/10 transition-all duration-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 py-3 px-4 rounded-xl font-bold text-sm text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: isLoading
                ? 'rgba(255,255,255,0.2)'
                : 'linear-gradient(180deg, #D946EF 0%, #7B2CBF 100%)',
              boxShadow: isLoading
                ? 'none'
                : '0 0 15px 0 rgba(255, 255, 255, 0.30) inset, 0 0 20px 0 rgba(123, 44, 191, 0.40)',
            }}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Processing...
              </span>
            ) : (
              'Confirm Deposit'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualDepositWarningModal;
